import { AuthService } from '../auth/AuthService';
import { ApiService } from '../utils/ApiService';

export interface UserSettings {
  autoRefresh: boolean;
  refreshInterval: number;
  cacheSettings: {
    enabled: boolean;
    maxAge: number;
  };
  display: {
    theme: 'light' | 'dark' | 'auto';
    showLoadingIndicators: boolean;
    compactMode: boolean;
  };
  performance: {
    batchSize: number;
    maxConcurrentRequests: number;
    enableRequestDeduplication: boolean;
  };
  notifications: {
    showSuccessMessages: boolean;
    showErrorMessages: boolean;
    autoHideTimeout: number;
  };
}

export const DEFAULT_SETTINGS: UserSettings = {
  autoRefresh: false,
  refreshInterval: 300000, // 5 minutes
  cacheSettings: {
    enabled: true,
    maxAge: 30000 // 30 seconds
  },
  display: {
    theme: 'auto',
    showLoadingIndicators: true,
    compactMode: false
  },
  performance: {
    batchSize: 50,
    maxConcurrentRequests: 10,
    enableRequestDeduplication: true
  },
  notifications: {
    showSuccessMessages: true,
    showErrorMessages: true,
    autoHideTimeout: 5000
  }
};

export type ViewType = 'login' | 'dashboard' | 'settings' | 'formulas' | 'entries' | 'aliases' | 'journal';

// A connection check that hangs for the default 60s is worse than one that says
// "Unavailable" — the liveness probe does no I/O and answers in milliseconds.
const HEALTH_CHECK_TIMEOUT_MS = 5000;

export class ViewManager {
  private currentView: ViewType = 'login';
  private settings: UserSettings;
  private sessionEndedHandler: (() => void) | null = null;

  constructor() {
    this.settings = this.loadSettings();
    this.initializeViewHandling();
  }

  private initializeViewHandling(): void {
    // Check URL parameters for initial view
    const urlParams = new URLSearchParams(window.location.search);
    const viewParam = urlParams.get('view') as ViewType;
    
    if (viewParam && this.isValidView(viewParam)) {
      this.currentView = viewParam;
    }
  }

  private isValidView(view: string): view is ViewType {
    return ['login', 'dashboard', 'settings', 'formulas', 'entries', 'aliases', 'journal'].includes(view);
  }

  /** Called when the dashboard finds the session can no longer be renewed. */
  setSessionEndedHandler(handler: () => void): void {
    this.sessionEndedHandler = handler;
  }

  showView(view: ViewType): void {
    this.hideAllViews();
    this.currentView = view;
    
    const viewElement = document.getElementById(`${view}-view`);
    if (viewElement) {
      viewElement.style.display = 'block';
      this.onViewShown(view);
    }
  }

  private hideAllViews(): void {
    const views: ViewType[] = ['login', 'dashboard', 'settings', 'formulas', 'entries', 'aliases', 'journal'];
    views.forEach(view => {
      const element = document.getElementById(`${view}-view`);
      if (element) {
        element.style.display = 'none';
      }
    });
  }

  private onViewShown(view: ViewType): void {
    switch (view) {
      case 'settings':
        this.initializeSettingsView();
        break;
      case 'dashboard':
        this.initializeStatusView();
        break;
    }
  }

  private initializeSettingsView(): void {
    this.populateSettingsForm();
    this.attachSettingsEventListeners();
  }

  private populateSettingsForm(): void {
    // Auto refresh settings
    const autoRefreshToggle = document.getElementById('auto-refresh-toggle') as HTMLInputElement;
    const refreshInterval = document.getElementById('refresh-interval') as HTMLInputElement;
    
    if (autoRefreshToggle) autoRefreshToggle.checked = this.settings.autoRefresh;
    if (refreshInterval) refreshInterval.value = (this.settings.refreshInterval / 1000).toString();

    // Cache settings
    const cacheEnabled = document.getElementById('cache-enabled') as HTMLInputElement;
    const cacheMaxAge = document.getElementById('cache-max-age') as HTMLInputElement;
    
    if (cacheEnabled) cacheEnabled.checked = this.settings.cacheSettings.enabled;
    if (cacheMaxAge) cacheMaxAge.value = (this.settings.cacheSettings.maxAge / 1000).toString();

    // Display settings
    const themeSelect = document.getElementById('theme-select') as HTMLSelectElement;
    const showLoadingIndicators = document.getElementById('show-loading-indicators') as HTMLInputElement;
    const compactMode = document.getElementById('compact-mode') as HTMLInputElement;
    
    if (themeSelect) themeSelect.value = this.settings.display.theme;
    if (showLoadingIndicators) showLoadingIndicators.checked = this.settings.display.showLoadingIndicators;
    if (compactMode) compactMode.checked = this.settings.display.compactMode;

    // Performance settings
    const batchSize = document.getElementById('batch-size') as HTMLInputElement;
    const maxConcurrentRequests = document.getElementById('max-concurrent-requests') as HTMLInputElement;
    const enableDeduplication = document.getElementById('enable-deduplication') as HTMLInputElement;
    
    if (batchSize) batchSize.value = this.settings.performance.batchSize.toString();
    if (maxConcurrentRequests) maxConcurrentRequests.value = this.settings.performance.maxConcurrentRequests.toString();
    if (enableDeduplication) enableDeduplication.checked = this.settings.performance.enableRequestDeduplication;

    // Notification settings
    const showSuccessMessages = document.getElementById('show-success-messages') as HTMLInputElement;
    const showErrorMessages = document.getElementById('show-error-messages') as HTMLInputElement;
    const autoHideTimeout = document.getElementById('auto-hide-timeout') as HTMLInputElement;
    
    if (showSuccessMessages) showSuccessMessages.checked = this.settings.notifications.showSuccessMessages;
    if (showErrorMessages) showErrorMessages.checked = this.settings.notifications.showErrorMessages;
    if (autoHideTimeout) autoHideTimeout.value = (this.settings.notifications.autoHideTimeout / 1000).toString();
  }

  private attachSettingsEventListeners(): void {
    // Save settings button
    const saveBtn = document.getElementById('save-settings-btn');
    saveBtn?.addEventListener('click', () => this.saveSettings());

    // Reset settings button
    const resetBtn = document.getElementById('reset-settings-btn');
    resetBtn?.addEventListener('click', () => this.resetSettings());

    // Import/Export buttons
    const exportBtn = document.getElementById('export-settings-btn');
    exportBtn?.addEventListener('click', () => this.exportSettings());

    const importBtn = document.getElementById('import-settings-btn');
    importBtn?.addEventListener('click', () => this.importSettings());
  }

  private saveSettings(): void {
    // Collect form data
    const autoRefreshToggle = document.getElementById('auto-refresh-toggle') as HTMLInputElement;
    const refreshInterval = document.getElementById('refresh-interval') as HTMLInputElement;
    const cacheEnabled = document.getElementById('cache-enabled') as HTMLInputElement;
    const cacheMaxAge = document.getElementById('cache-max-age') as HTMLInputElement;
    const themeSelect = document.getElementById('theme-select') as HTMLSelectElement;
    const showLoadingIndicators = document.getElementById('show-loading-indicators') as HTMLInputElement;
    const compactMode = document.getElementById('compact-mode') as HTMLInputElement;
    const batchSize = document.getElementById('batch-size') as HTMLInputElement;
    const maxConcurrentRequests = document.getElementById('max-concurrent-requests') as HTMLInputElement;
    const enableDeduplication = document.getElementById('enable-deduplication') as HTMLInputElement;
    const showSuccessMessages = document.getElementById('show-success-messages') as HTMLInputElement;
    const showErrorMessages = document.getElementById('show-error-messages') as HTMLInputElement;
    const autoHideTimeout = document.getElementById('auto-hide-timeout') as HTMLInputElement;

    // Update settings object
    this.settings = {
      autoRefresh: autoRefreshToggle?.checked || false,
      refreshInterval: parseInt(refreshInterval?.value || '300') * 1000,
      cacheSettings: {
        enabled: cacheEnabled?.checked || true,
        maxAge: parseInt(cacheMaxAge?.value || '30') * 1000
      },
      display: {
        theme: (themeSelect?.value as 'light' | 'dark' | 'auto') || 'auto',
        showLoadingIndicators: showLoadingIndicators?.checked || true,
        compactMode: compactMode?.checked || false
      },
      performance: {
        batchSize: parseInt(batchSize?.value || '50'),
        maxConcurrentRequests: parseInt(maxConcurrentRequests?.value || '10'),
        enableRequestDeduplication: enableDeduplication?.checked || true
      },
      notifications: {
        showSuccessMessages: showSuccessMessages?.checked || true,
        showErrorMessages: showErrorMessages?.checked || true,
        autoHideTimeout: parseInt(autoHideTimeout?.value || '5') * 1000
      }
    };

    // Store settings
    this.storeSettings(this.settings);
    this.showNotification('Settings saved successfully!', 'success');
  }

  private resetSettings(): void {
    this.settings = { ...DEFAULT_SETTINGS };
    this.storeSettings(this.settings);
    this.populateSettingsForm();
    this.showNotification('Settings reset to defaults', 'info');
  }

  private exportSettings(): void {
    const blob = new Blob([JSON.stringify(this.settings, null, 2)], {
      type: 'application/json'
    });
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bifrost-settings.json';
    a.click();
    URL.revokeObjectURL(url);
    
    this.showNotification('Settings exported successfully', 'success');
  }

  private importSettings(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const imported = JSON.parse(e.target?.result as string);
          this.settings = { ...DEFAULT_SETTINGS, ...imported };
          this.storeSettings(this.settings);
          this.populateSettingsForm();
          this.showNotification('Settings imported successfully', 'success');
        } catch {
          this.showNotification('Invalid settings file', 'error');
        }
      };
      reader.readAsText(file);
    };
    
    input.click();
  }

  private initializeStatusView(): void {
    this.updateStatusInformation();
  }

  private async updateStatusInformation(): Promise<void> {
    const statusContent = document.getElementById('status-content');
    if (!statusContent) return;

    statusContent.innerHTML = '<p class="connection-checking">Checking…</p>';

    try {
      const status = await this.getSystemStatus();

      if (!status.authenticated && this.sessionEndedHandler && this.currentView === 'dashboard') {
        this.sessionEndedHandler();
        return;
      }

      const authClass = status.authenticated ? 'connection-ok' : 'connection-bad';
      const authText = status.authenticated ? 'Connected' : 'Not authenticated';

      const apiClass = status.apiHealthy ? 'connection-ok' : 'connection-bad';
      const apiText = status.apiHealthy ? 'Connected' : 'Unavailable';

      const version = process.env.BUILD_VERSION || 'unknown';
      const sha = process.env.BUILD_SHA || 'unknown';
      const buildTime = process.env.BUILD_TIME || '';
      const buildDate = buildTime ? new Date(buildTime).toISOString().substring(0, 16).replace('T', ' ') : 'unknown';

      statusContent.innerHTML = `
        <dl class="connection-facts">
          <dt>Authentication</dt>
          <dd class="${authClass}">${authText}</dd>
          <dt>API</dt>
          <dd class="${apiClass}">${apiText}${status.apiResponseTime ? ` <span class="connection-latency">${status.apiResponseTime} ms</span>` : ''}</dd>
        </dl>
        <p class="status-build-info">
          <span>v${version}</span>
          <span aria-hidden="true">·</span>
          <span title="Git commit">${sha}</span>
          <span aria-hidden="true">·</span>
          <span title="Build time (UTC)">${buildDate}</span>
        </p>
      `;
    } catch {
      statusContent.innerHTML = '<p class="connection-bad">Could not check the connection.</p>';
    }
  }

  private async getSystemStatus(): Promise<{
    authenticated: boolean;
    apiHealthy: boolean;
    apiResponseTime?: number;
  }> {
    const result = {
      authenticated: false,
      apiHealthy: false,
      apiResponseTime: undefined as number | undefined
    };

    // Ask the same question the custom functions ask, so the card can't
    // disagree with what the cells are doing. The token lives in
    // OfficeRuntime.storage, not localStorage.
    try {
      result.authenticated = await new AuthService().isAuthenticated();
    } catch {
      result.authenticated = false;
    }

    // Must go through ApiService: a bare relative URL resolves against the
    // Static Web App serving this pane, which answers 200 to anything and
    // reports a healthy API even when the backend is unreachable.
    try {
      const startTime = Date.now();
      const response = await new ApiService().get('/health/live', { timeout: HEALTH_CHECK_TIMEOUT_MS });
      result.apiResponseTime = Date.now() - startTime;
      result.apiHealthy = response.success;
    } catch {
      result.apiHealthy = false;
    }

    return result;
  }

  getCurrentView(): ViewType {
    return this.currentView;
  }

  getSettings(): UserSettings {
    return { ...this.settings };
  }

  private loadSettings(): UserSettings {
    try {
      const stored = localStorage.getItem('bifrost-settings');
      if (stored) {
        return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) };
      }
    } catch (error) {
      console.warn('Failed to load settings from localStorage:', error);
    }
    return { ...DEFAULT_SETTINGS };
  }

  private storeSettings(settings: UserSettings): void {
    try {
      localStorage.setItem('bifrost-settings', JSON.stringify(settings));
    } catch (error) {
      console.error('Failed to store settings:', error);
    }
  }

  private authHeaders: Record<string, string> = {};

  updateSandboxBanner(
    active: boolean,
    authHeaders?: Record<string, string>,
    demoContext?: {
      isDemoTemplate: boolean;
      homeCompanyName?: string;
      activeCompanyName?: string;
      activeCompanyShortName?: string | null;
      activeCompanyIndustryType?: string | null;
    },
  ): void {
    if (authHeaders) this.authHeaders = authHeaders;
    let banner = document.getElementById('sandbox-banner');

    // A session is in demo exactly when it has switched into a demo template
    // company; switching back to the real company removes the banner.
    const showBanner = active && demoContext?.isDemoTemplate === true;

    if (!showBanner) {
      // parentNode.removeChild, not remove(): the desktop ES5 fallback has no ChildNode.remove.
      banner?.parentNode?.removeChild(banner);
      const sampleBtn = document.getElementById('load-sample-btn');
      sampleBtn?.parentNode?.removeChild(sampleBtn);
      return;
    }

    if (!banner) {
      banner = document.createElement('div');
      banner.id = 'sandbox-banner';
      const container = document.getElementById('app') || document.body;
      container.insertBefore(banner, container.firstChild);
    }

    // Industry sets the accent color (CSS in main.css). Falls back to neutral
    // amber when industry is unknown or the company is non-industry-tagged.
    const industry = demoContext.activeCompanyIndustryType || 'unknown';
    banner.className = `bifrost-demo-banner industry-${industry}`;
    banner.removeAttribute('style');

    const targetName = demoContext.activeCompanyName ?? 'demo company';
    const industryLabel = this.formatIndustry(industry);
    const shortName = demoContext.activeCompanyShortName;

    // Build banner content as DOM nodes (avoid innerHTML so user-supplied
    // company names can never inject markup \u2014 names come from the DB but
    // safer to treat as untrusted).
    // textContent, not replaceChildren(): the desktop ES5 fallback has no replaceChildren.
    banner.textContent = '';

    const heading = document.createElement('div');
    heading.className = 'bifrost-demo-banner__title';
    heading.textContent = `Viewing ${targetName}${industryLabel ? ` \u00b7 ${industryLabel} demo` : ' \u00b7 Demo'} \u2014 formulas run against this company's sample data.`;
    banner.appendChild(heading);

    if (shortName) {
      const tip = document.createElement('div');
      tip.className = 'bifrost-demo-banner__tip';
      tip.textContent = `Tip: from your own company, prefix any formula with `;
      const code = document.createElement('code');
      code.textContent = `${shortName}:`;
      tip.appendChild(code);
      tip.appendChild(document.createTextNode(' to query without switching.'));
      banner.appendChild(tip);
    }

    let sampleBtn = document.getElementById('load-sample-btn') as HTMLButtonElement | null;
    if (!sampleBtn) {
      sampleBtn = document.createElement('button');
      sampleBtn.id = 'load-sample-btn';
      sampleBtn.textContent = 'Load sample formulas';
      sampleBtn.className = 'bifrost-demo-banner__sample-btn';
      sampleBtn.addEventListener('click', () => {
        const btn = sampleBtn as HTMLButtonElement;
        void this.loadSampleFormulas(btn);
      });
      // insertBefore the next sibling, not after(): the desktop ES5 fallback has no ChildNode.after.
      banner.parentNode?.insertBefore(sampleBtn, banner.nextSibling);
    }
  }

  private formatIndustry(industry: string | null | undefined): string {
    if (!industry || industry === 'unknown') return '';
    return industry.charAt(0).toUpperCase() + industry.slice(1);
  }

  private async loadSampleFormulas(button?: HTMLButtonElement): Promise<void> {
    // UX: button feedback so users know the click registered. Without this
    // the button looks idle while the backend call (~300ms warm, 1.3s cold)
    // and the Excel.run write phase happen.
    const originalText = button?.textContent ?? 'Load sample formulas';
    if (button) {
      button.disabled = true;
      button.textContent = 'Loading…';
    }
    try {
      const { ApiService } = await import('../utils/ApiService');
      const api = new ApiService();
      const response = await api.get<{ sheetName?: string; formulas: { label: string; formula: string }[]; drilldown?: { startRow?: number; label: string; formula: string } }>('/erp-connections/sandbox/sample-sheet', {
        headers: this.authHeaders,
      });
      const sampleData = response?.data;
      if (!sampleData?.formulas) {
        console.error('Sample sheet response:', JSON.stringify(response));
        this.showNotification('Could not load sample formulas', 'error');
        return;
      }

      // An empty list means this company has no demo formulas to offer. Tell the
      // user explicitly instead of silently dropping a headers-only sheet.
      if (sampleData.formulas.length === 0) {
        this.showNotification('No sample formulas are available for this company yet', 'info');
        return;
      }

      if (button) button.textContent = 'Building sheet…';

      // [PERF-ADDIN] Track each phase of Excel.run separately so we can
      // see where time goes on Excel Desktop (Mac/Win), where this flow
      // takes ~15s vs ~instant in Excel Online. Tagged with a clear marker
      // so removal is grep-trivial.
      const _t0 = Date.now();
      let _tFirstSync = 0;
      let _tDeleteSync = 0;
      let _tFinalSync = 0;
      let _hadExisting = false;

      await Excel.run(async (context) => {
        const sheets = context.workbook.worksheets;
        const sheetName = sampleData.sheetName || 'Bifrost Demo';

        // Suspend Excel's calculation engine for the duration of this
        // Excel.run. Otherwise on Excel Desktop, every formula write
        // can trigger an immediate recalc round-trip, which is the
        // suspected cause of the 15s delay before the sheet appears.
        // Calculation resumes automatically at the next sync.
        if (context.application.suspendApiCalculationUntilNextSync) {
          context.application.suspendApiCalculationUntilNextSync();
        }

        sheets.load('items/name');
        const _tSyncStart = Date.now();
        await context.sync();
        _tFirstSync = Date.now() - _tSyncStart;

        const existing = sheets.items.find(s => s.name === sheetName);
        if (existing) {
          _hadExisting = true;
          existing.delete();
          const _tDelStart = Date.now();
          await context.sync();
          _tDeleteSync = Date.now() - _tDelStart;
        }

        const sheet = sheets.add(sheetName);

        const headerRange = sheet.getRange('A1:B1');
        headerRange.values = [['Label', 'Result']];
        headerRange.format.font.bold = true;

        const formulas = sampleData.formulas;
        // Block range writes — one for labels, one for formulas — instead of
        // 2N individual getRange calls in a loop. Each getRange goes through
        // the Office.js proxy and adds JS-side overhead even when the network
        // payload is the same; for 10-20 formulas this saves ~50-200ms.
        if (formulas.length > 0) {
          const lastRow = formulas.length + 1; // headers occupy row 1
          const labelValues = formulas.map(f => [f.label]);
          const formulaValues = formulas.map(f => [f.formula]);
          sheet.getRange(`A2:A${lastRow}`).values = labelValues;
          sheet.getRange(`B2:B${lastRow}`).formulas = formulaValues;
        }

        const drilldown = sampleData.drilldown;
        if (drilldown) {
          const drilldownRow = drilldown.startRow || formulas.length + 4;
          const drilldownLabel = sheet.getRange(`A${drilldownRow}`);
          drilldownLabel.values = [[drilldown.label]];
          drilldownLabel.format.font.bold = true;
          sheet.getRange(`A${drilldownRow + 1}`).formulas = [[drilldown.formula]];
        }

        // Autofit a fixed range instead of getUsedRange(). getUsedRange
        // forces Excel to compute the populated rectangle, which on
        // Desktop can wait for in-flight custom function evaluations.
        // We know exactly which columns we wrote — fit those.
        sheet.getRange('A:B').format.autofitColumns();
        sheet.activate();
        const _tFinalStart = Date.now();
        await context.sync();
        _tFinalSync = Date.now() - _tFinalStart;
      });

      // [PERF-ADDIN] intentional perf instrumentation, remove together
      console.info('[PERF-ADDIN] loadSampleFormulas Excel.run', {
        ms_total: Date.now() - _t0,
        ms_firstSync: _tFirstSync,
        ms_deleteSync: _tDeleteSync,
        ms_finalSync: _tFinalSync,
        hadExistingSheet: _hadExisting,
        formulaCount: sampleData.formulas?.length ?? 0,
      });

      this.showNotification('Sample formulas loaded!', 'success');
    } catch (error) {
      console.error('Failed to load sample formulas:', error);
      this.showNotification(`Failed to load sample formulas: ${error instanceof Error ? error.message : String(error)}`, 'error');
    } finally {
      if (button) {
        button.disabled = false;
        button.textContent = originalText;
      }
    }
  }

  private showNotification(message: string, type: 'success' | 'error' | 'info' | 'warning'): void {
    const notification = document.createElement('div');
    notification.className = `notification notification-${type}`;
    notification.setAttribute('role', type === 'error' ? 'alert' : 'status');
    notification.textContent = message;

    document.body.appendChild(notification);

    // Auto-hide after timeout
    setTimeout(() => {
      notification.parentNode?.removeChild(notification);
    }, this.settings.notifications.autoHideTimeout);
  }

  /**
   * Public wrapper for showNotification — used by callers outside ViewManager
   * (e.g. company-switch confirmation in taskpane.ts).
   */
  notify(message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info'): void {
    this.showNotification(message, type);
  }
}